/**
 * The operator roster — who can sign into this panel, and with what power.
 *
 * These are the only accounts that approve a payout, so the module is written
 * around three properties rather than around convenience:
 *
 *   **No credential ever comes back.** The server's `OperatorView` carries no
 *   password hash and no TOTP secret — `totpEnabled` is a boolean derived from
 *   a timestamp. Nothing in this file should ever grow a field that changes
 *   that, and nothing here logs a body.
 *
 *   **A new or reset password is a live credential in transit.** It goes into
 *   one request and is never stored, toasted, put in a URL, or handed to
 *   `useAsync` (whose result outlives the render that asked for it). The same
 *   rule `recovery.ts` keeps for a reset link.
 *
 *   **Hiding a control is not the control.** Every route below is gated on the
 *   server by `requireOperator('admin')`, and the server also refuses an
 *   operator's attempt to change their own role or status. The UI mirrors both
 *   refusals so the operator is not surprised, but the server is what enforces
 *   them.
 *
 * MOCK MODE HAS NO FIXTURES HERE, deliberately — the same call `recovery.ts`
 * and the key manager make. An invented roster teaches an admin to trust names
 * that do not exist, and a fake "suspended" is a fake safety. The read answers
 * empty; a write goes live and fails honestly.
 */
import { http } from './http.ts';
import { MOCK_MODE } from './client.ts';
import type { OperatorRole } from './auth.ts';

export type { OperatorRole } from './auth.ts';

export type OperatorStatus = 'active' | 'suspended';

/** One row of the roster. Mirrors `OperatorView` on the server exactly. */
export interface OperatorAccount {
  id: number;
  username: string;
  email: string;
  role: OperatorRole;
  status: OperatorStatus;
  /** True once a second factor has been CONFIRMED. A pending secret is not enrollment. */
  totpEnabled: boolean;
  createdAt: string;
  lastLoginAt: string | null;
}

export interface NewOperator {
  username: string;
  email: string;
  role: OperatorRole;
  /** Twelve characters minimum — the server's floor for an account that releases money. */
  password: string;
}

/** Exactly one of these three, per call. The server refuses an empty patch. */
export interface OperatorPatch {
  role?: OperatorRole;
  status?: OperatorStatus;
  password?: string;
}

/** What each role may do, in the words the panel uses to explain a promotion. */
export const ROLE_NOTES: Record<OperatorRole, string> = {
  viewer: 'Reads every queue and record. Approves nothing and changes nothing.',
  approver: 'Everything a viewer can do, plus signing off deposits and payouts.',
  admin: 'Everything, plus this roster, the keys, the edge editor and the lobby.',
};

/** Least privilege, ascending — the same ranking the server enforces by rank. */
export const ROLES: OperatorRole[] = ['viewer', 'approver', 'admin'];

/**
 * LIVE — `GET /operator/operators`. Admin only, server-side.
 *
 * Returns the whole roster; it is a handful of rows and paginating it would be
 * machinery for nothing.
 */
export async function fetchOperators(): Promise<OperatorAccount[]> {
  if (MOCK_MODE) return [];
  const body = await http.get<{ operators: OperatorAccount[] }>('/operator/operators');
  return body.operators;
}

/**
 * LIVE — `POST /operator/operators`. Admin only, audited as `operator.create`.
 *
 * The password is a live credential: it is passed in, sent once, and must not
 * be retained by the caller after this resolves.
 */
export async function createOperator(input: NewOperator): Promise<OperatorAccount> {
  const body = await http.post<{ operator: OperatorAccount }>('/operator/operators', {
    username: input.username,
    email: input.email,
    role: input.role,
    password: input.password,
  });
  return body.operator;
}

/**
 * LIVE — `PATCH /operator/operators/:id`. Admin only, audited as
 * `operator.update` or `operator.suspend`.
 *
 * Every one of the three changes revokes that operator's live sessions on the
 * server, so a demotion or a suspension bites on their very next request rather
 * than at token expiry.
 */
export async function updateOperator(
  id: number,
  patch: OperatorPatch,
): Promise<OperatorAccount> {
  const body = await http.patch<{ operator: OperatorAccount }>(`/operator/operators/${id}`, patch);
  return body.operator;
}

/**
 * A password to hand to a new operator, or to force onto a locked-out one.
 *
 * From `crypto.getRandomValues`, never `Math.random` — this is a credential for
 * an account that releases real money, and a predictable one is not a password.
 * Rejection sampling on a 64-character alphabet so every character is uniform;
 * a plain modulo would quietly bias the first few letters.
 */
const ALPHABET = 'ABCDEFGHJKLMNPQRSTUVWXYZabcdefghijkmnopqrstuvwxyz23456789-_';

export function suggestPassword(length = 20): string {
  const out: string[] = [];
  const bytes = new Uint8Array(length * 2);
  while (out.length < length) {
    crypto.getRandomValues(bytes);
    for (const byte of bytes) {
      // 256 is not a multiple of the alphabet length, so the tail is discarded
      // rather than folded back on top of the first characters.
      if (byte >= 256 - (256 % ALPHABET.length)) continue;
      out.push(ALPHABET[byte % ALPHABET.length]!);
      if (out.length === length) break;
    }
  }
  return out.join('');
}

/** The server's floor. Stated here so the form can refuse before the round trip. */
export const MIN_PASSWORD_LENGTH = 12;
